import { FormFieldProps } from '../Form.types';
import type { Locale } from '../../../types';
import {
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  OPTION_LABELS,
  VALIDATION_MESSAGES,
  SUBMIT_MESSAGES,
  LocalizedText,
  ValidationMessageKey,
  SubmitMessageKey,
} from './translations';

const DEFAULT_LOCALE: Locale = 'en-US';

// `locale` is undefined wherever the component renders outside a Next router with i18n
// configured — Storybook, and Cypress mounts that pass no router locale.
const translate = (text: LocalizedText, locale?: string): string => text[locale as Locale] ?? text[DEFAULT_LOCALE];

/** Falls back to the definition's own `placeholder` for any field missing from FIELD_PLACEHOLDERS. */
export const getLocalizedFieldPlaceholder = (field: FormFieldProps, locale?: string): string | undefined => {
  const placeholder = field.id ? FIELD_PLACEHOLDERS[field.id] : undefined;
  return placeholder ? translate(placeholder, locale) : field.placeholder;
};

/**
 * Display text for a select option. Falls back to the value itself, which is deliberate — the
 * value is the Salesforce picklist key and stays English whether or not a translation exists.
 */
export const getLocalizedOptionLabel = (option: string, locale?: string): string => {
  const label = OPTION_LABELS[option];
  return label ? translate(label, locale) : option;
};

/** Falls back to the definition's English `label` for any field missing from FIELD_LABELS. */
export const getLocalizedFieldLabel = (field: FormFieldProps, locale?: string): string | undefined => {
  const label = field.id ? FIELD_LABELS[field.id] : undefined;
  return label ? translate(label, locale) : field.label;
};

export const getValidationMessage = (
  key: ValidationMessageKey,
  locale?: string,
  params: Record<string, string | number> = {},
): string =>
  Object.entries(params).reduce<string>(
    (message, [name, value]) => message.replace(`{${name}}`, String(value)),
    translate(VALIDATION_MESSAGES[key], locale),
  );

export const getSubmitMessage = (key: SubmitMessageKey, locale?: string): string =>
  translate(SUBMIT_MESSAGES[key], locale);
